import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import { getSocietyBook, refreshSocietyBook } from "../../services/orgService";
import { errorToast, successToast } from "../Toasts/Toasts";

// Every society we know, and how engaged each one actually is.
//
// The bands are ordered by what the evidence proves, not by how busy a row
// looks: money billed, then an ask made, then a door opened. The
// most-visited societies here have often said "not right now" nine times,
// so sorting on visits would put them at the top - which is backwards.
//
// The CRM is master. Nothing on this screen writes back to it; Refresh pulls
// across again, and the time of the last pull is printed rather than hidden,
// because a cached book whose age is invisible goes on being believed.

export const BANDS = {
  customer: { label: "Customer", cls: "bg-success", hint: "has been invoiced in the last two years" },
  lapsed: { label: "Lapsed", cls: "bg-warning text-dark", hint: "bought once, nothing since" },
  in_play: { label: "In play", cls: "bg-primary", hint: "asked for a price, no sale yet" },
  in_touch: { label: "In touch", cls: "bg-info text-dark", hint: "we have been in, nothing asked for" },
  cold: { label: "Cold", cls: "bg-secondary", hint: "the door is shut for now" },
  not_reached: { label: "Not reached", cls: "bg-light text-dark border", hint: "never visited" },
};

const inr = (n) => (n === null || n === undefined ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);

export function BandBadge({ band }) {
  const b = BANDS[band];
  if (!b) return null;
  return <span className={`badge ${b.cls}`} title={b.hint}>{b.label}</span>;
}

function ago(days) {
  if (days === null || days === undefined) return "never";
  if (days < 1) return "today";
  if (days < 45) return `${days} d`;
  if (days < 400) return `${Math.round(days / 30)} mo`;
  return `${(days / 365).toFixed(1)} yr`;
}

const SocietyBook = () => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [band, setBand] = useState("");
  const [coverage, setCoverage] = useState("");
  const [sort, setSort] = useState("engagement");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await getSocietyBook({ q: q || undefined, band: band || undefined, coverage: coverage || undefined, sort });
      setData(r.data || r);
    } catch (e) {
      errorToast("Could not load the account book");
    }
  }, [q, band, coverage, sort]);

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await refreshSocietyBook();
      const d = r.data || r;
      successToast(`${d.accounts} societies, ${d.visits} visits, ${d.booksLinked} linked to Books`);
      load();
    } catch (e) {
      errorToast(e?.response?.data?.message || "Refresh failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const rows = data.rows || [];
  const bandFacet = (data.facets && data.facets.band) || [];
  const paying = rows.filter((r) => r.invoiceCount > 0);
  const billed = paying.reduce((s, r) => s + Number(r.billedTotal || 0), 0);
  const due = paying.reduce((s, r) => s + Number(r.amountDue || 0), 0);

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row-title px-2 d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h6 className="fw-bold mb-0">Societies</h6>
            <div className="small text-muted">
              {rows.length} shown · {paying.length} have been invoiced · {inr(billed)} billed, {inr(due)} still owed
            </div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <span className="small text-muted">
              {data.syncedAt ? `from the CRM ${new Date(data.syncedAt).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : "never pulled"}
            </span>
            <Button size="sm" variant="outline-success" disabled={busy} onClick={refresh}>
              {busy ? "Pulling…" : "Refresh"}
            </Button>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 px-2 mt-2">
          {bandFacet.map((b) => (
            <button
              key={b.value}
              type="button"
              className={`btn btn-sm ${band === b.value ? "btn-dark" : "btn-outline-secondary"}`}
              onClick={() => setBand(band === b.value ? "" : b.value)}
              title={BANDS[b.value]?.hint}
            >
              {BANDS[b.value]?.label || b.value} <span className="badge bg-light text-dark ms-1">{b.count}</span>
            </button>
          ))}
        </div>

        <div className="d-flex flex-wrap gap-2 px-2 mt-2">
          <Form.Control size="sm" style={{ maxWidth: 240 }} placeholder="Search a society…" value={q} onChange={(e) => setQ(e.target.value)} />
          <Form.Select size="sm" style={{ maxWidth: 190 }} value={coverage} onChange={(e) => setCoverage(e.target.value)}>
            <option value="">Any coverage</option>
            {((data.facets && data.facets.coverage) || []).map((c) => (
              <option key={c.value} value={c.value}>{c.value} ({c.count})</option>
            ))}
          </Form.Select>
          <Form.Select size="sm" style={{ maxWidth: 190 }} value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="engagement">Most engaged first</option>
            <option value="billed">Most billed first</option>
            <option value="lastVisit">Last visited first</option>
            <option value="visits">Most visits first</option>
            <option value="distance">Nearest first</option>
            <option value="name">By name</option>
          </Form.Select>
        </div>

        <div className="card mt-2">
          <div className="card-body p-0">
            <div className="table-responsive table-scroll">
              <table className="table table-sm mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Society</th>
                    <th>Engagement</th>
                    <th className="text-end">Billed</th>
                    <th className="text-end">Owed</th>
                    <th className="text-end">Visits</th>
                    <th>Last seen</th>
                    <th>Where it stands</th>
                    <th className="text-end">Distance</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.zohoAccountId}>
                      <td className="small">
                        <Link to={`/societies/${r.zohoAccountId}`}>{r.name}</Link>
                        {r.organization && <span className="badge bg-dark ms-1" title="on the portal">portal</span>}
                        {r.seriousRisk && <span className="badge bg-danger ms-1">risk</span>}
                        {r.city && <div className="text-muted">{r.city}</div>}
                      </td>
                      <td><BandBadge band={r.engagementBand} /></td>
                      <td className="text-end small">{r.invoiceCount > 0 ? inr(r.billedTotal) : <span className="text-muted">—</span>}</td>
                      <td className="text-end small">{Number(r.amountDue) > 0 ? <span className="text-danger">{inr(r.amountDue)}</span> : <span className="text-muted">—</span>}</td>
                      <td className="text-end small">{r.visitCount || <span className="text-muted">0</span>}</td>
                      <td className="small text-muted">{ago(r.daysSinceVisit)}</td>
                      <td className="small text-muted">{r.lastVisitOutcome || "—"}</td>
                      <td className="text-end small text-muted">{r.distanceKm ? `${r.distanceKm} km` : "—"}</td>
                    </tr>
                  ))}
                  {!rows.length && (
                    <tr>
                      <td colSpan={8} className="text-muted p-3">
                        Nothing here yet. Press Refresh to pull the societies across from the CRM.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="small text-muted mt-2 px-2">
          The CRM stays the master record — edits belong there, and the next Refresh brings them over. Engagement is worked out
          from what can be proved: money billed first, then a price asked for, then a door opened.
        </div>
      </div>
    </div>
  );
};

export default SocietyBook;
